import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Form,
  Input,
  Button,
  Switch,
  Row,
  Col,
  Select,
  Divider
} from 'antd';
import {
  SaveOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  PhoneOutlined,
  GlobalOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import {
  createDeliveryAddressRequest,
  updateDeliveryAddressRequest
} from '../../../redux/actions/deliveryAddressActions';
import '../../../styles/forms.css';

const { Option } = Select;
const { TextArea } = Input;

const DeliveryAddressForm = ({ onCancel }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { editingAddress, creating, updating } = useSelector(state => state.deliveryAddresses);

  const isEditing = !!editingAddress;
  const isSubmitting = creating || updating;

  useEffect(() => {
    if (editingAddress) {
      form.setFieldsValue({
        unit_name: editingAddress.unit_name,
        address: editingAddress.address,
        city: editingAddress.city,
        state: editingAddress.state,
        pincode: editingAddress.pincode,
        country: editingAddress.country || 'India',
        phone: editingAddress.phone,
        is_default: editingAddress.is_default || false,
        is_active: editingAddress.is_active !== undefined ? editingAddress.is_active : true
      });
    } else {
      form.setFieldsValue({
        country: 'India',
        is_default: false,
        is_active: true
      });
    }
  }, [editingAddress, form]);

  const handleSubmit = (values) => {
    const addressData = {
      ...values,
      country: values.country || 'India'
    };

    if (isEditing) {
      dispatch(updateDeliveryAddressRequest(editingAddress.id, addressData));
    } else {
      dispatch(createDeliveryAddressRequest(addressData));
    }

    // Close modal after successful submission (handled in saga)
    onCancel();
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  // Validation rules
  const validatePincode = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('Pincode is required'));
    }
    if (!/^\d{6}$/.test(value)) {
      return Promise.reject(new Error('Pincode must be exactly 6 digits'));
    }
    return Promise.resolve();
  };

  // Validation rules for phone
  const validatePhone = (_, value) => {
    if (!value) {
      return Promise.resolve(); // Phone is optional
    }
    // Regex for international phone format: +country-code followed by digits
    const phoneRegex = /^\+\d{1,3}-\d{10}$/;
    if (!phoneRegex.test(value)) {
      return Promise.reject(new Error('Phone must be in format +country-code-10digits (e.g., +91-9876543210)'));
    }
    return Promise.resolve();
  };

  return (
    <div className="professional-form">
      {/* Form Header */}
      <div className="form-modal-header">
        <div className="form-modal-icon">
          <EnvironmentOutlined />
        </div>
        <div className="form-modal-title">
          <h3>{isEditing ? 'Edit Delivery Address' : 'New Delivery Address'}</h3>
          <p>Add delivery location for purchase orders</p>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
        requiredMark="optional"
      >
        {/* Unit Information */}
        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              label="Unit Name"
              name="unit_name"
              rules={[
                { required: true, message: 'Unit name is required' },
                { min: 2, message: 'Unit name must be at least 2 characters' },
                { max: 200, message: 'Unit name must not exceed 200 characters' }
              ]}
              tooltip="Name of the delivery location or unit"
            >
              <Input
                prefix={<HomeOutlined style={{ color: '#999' }} />}
                placeholder="e.g., Main Office, Warehouse A, Branch Office"
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              label="Complete Address"
              name="address"
              rules={[
                { required: true, message: 'Address is required' },
                { min: 10, message: 'Address must be at least 10 characters' },
                { max: 500, message: 'Address must not exceed 500 characters' }
              ]}
            >
              <TextArea
                rows={3}
                placeholder="123 Main Street, Block A, Near Central Mall"
                showCount
                maxLength={500}
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" style={{ margin: '1.5rem 0 1rem' }}>
          <span style={{ fontSize: '0.85rem', color: '#666' }}>Location Details</span>
        </Divider>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="City"
              name="city"
              rules={[
                { required: true, message: 'City is required' },
                { min: 2, message: 'City must be at least 2 characters' },
                { max: 100, message: 'City must not exceed 100 characters' }
              ]}
            >
              <Input placeholder="e.g., Mumbai, Delhi" size="large" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="State"
              name="state"
              rules={[
                { required: true, message: 'State is required' },
                { min: 2, message: 'State must be at least 2 characters' },
                { max: 100, message: 'State must not exceed 100 characters' }
              ]}
            >
              <Input placeholder="e.g., Maharashtra, Gujarat" size="large" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Pincode"
              name="pincode"
              rules={[{ validator: validatePincode }]}
              tooltip="6-digit postal code"
            >
              <Input
                placeholder="400001"
                maxLength={6}
                style={{ fontFamily: 'monospace' }}
                size="large"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Country"
              name="country"
              rules={[{ required: true, message: 'Country is required' }]}
            >
              <Select
                placeholder="Select country"
                size="large"
                suffixIcon={<GlobalOutlined />}
              >
                <Option value="India">🇮🇳 India</Option>
                <Option value="USA">🇺🇸 USA</Option>
                <Option value="Canada">🇨🇦 Canada</Option>
                <Option value="UK">🇬🇧 UK</Option>
                <Option value="Australia">🇦🇺 Australia</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              label="Phone Number (Optional)"
              name="phone"
              rules={[{ validator: validatePhone }]}
              tooltip="Contact number for this location"
            >
              <Input
                prefix={<PhoneOutlined style={{ color: '#999' }} />}
                placeholder="+91-9876543210"
                maxLength={20}
                size="large"
              />
            </Form.Item>
            <div className="input-help-text" style={{ marginTop: '-0.5rem', marginBottom: '1rem' }}>
              <InfoCircleOutlined />
              Format: +country-code-10digits
            </div>
          </Col>
        </Row>

        <Divider style={{ margin: '1rem 0' }} />

        <Row gutter={16}>
          <Col xs={24}>
            <div className="status-badge-group">
              <div className="status-badge-item">
                <span className="status-badge-label">Default Address</span>
                <Form.Item
                  name="is_default"
                  valuePropName="checked"
                  style={{ margin: 0 }}
                >
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>
              </div>
              <div className="status-badge-item">
                <span className="status-badge-label">Active Status</span>
                <Form.Item
                  name="is_active"
                  valuePropName="checked"
                  style={{ margin: 0 }}
                >
                  <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                </Form.Item>
              </div>
            </div>
          </Col>
        </Row>

        <div className="form-actions">
          <Button onClick={handleCancel} size="large">
            <CloseOutlined />
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={isSubmitting}
            icon={<SaveOutlined />}
            size="large"
          >
            {isEditing ? 'Update Address' : 'Add Address'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default DeliveryAddressForm;